import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent, KeyboardEvent, ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  Avatar,
  Button,
  Checkbox,
  EmptyState,
  Icon,
  IconButton,
  Input,
  Menu,
  Modal,
  PageHeader,
  Pill,
  Select,
  SegmentedControl,
  Sheet,
  Skeleton,
  SkeletonText,
  Textarea,
  useToast,
} from "@astrum/ui";
import type { MenuItem, PillTone } from "@astrum/ui";
import { api, ApiError, canDeleteTask } from "../lib/api.js";
import { usePersistentState } from "../lib/use-persistent-state.js";
import type { BoardColumnDef, Project, ProjectSection, Tag, Task as BaseTask } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { toCsv, downloadCsv } from "../lib/csv.js";
import { ExportMenu } from "../components/ExportMenu.js";
import { LifecycleLauncher, ProjectTemplateGallery } from "../components/Lifecycle.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { NewTaskDialog } from "../components/NewTaskDialog.js";
import { TagChip, useTags } from "../components/TagPicker.js";
import type { CustomField } from "@astrum/shared";
import { cfQueryValues, customCsvColumns } from "../lib/custom-fields.js";
import { TableView } from "../components/task-views/TableView.js";
import { CalendarView } from "../components/task-views/CalendarView.js";
import { TimelineView } from "../components/task-views/TimelineView.js";
import { SavedViewsMenu } from "../components/task-views/SavedViewsMenu.js";
import type { TaskPatch } from "../components/task-views/types.js";
import { paramsFromState, stateFromParams, VIEW_PARAM_KEYS } from "../lib/work-views.js";
import type { ViewState } from "../lib/work-views.js";
import "./work.css";

/**
 * `/tasks` hydrates a couple of fields as optional here that the shared `Task`
 * type declares as required, because not every endpoint feeding this surface
 * hydrates them.
 */
type Task = BaseTask & {
  projectCode?: string | null;
  assigneeAvatarUrl?: string | null;
  commentCount?: number;
  assignees?: BaseTask["assignees"];
};

/** Small warning shown while any of a task's blockers is still open. */
function BlockedMark({ count }: { count: number }) {
  const label = count === 1 ? "Blocked by 1 open task" : `Blocked by ${count} open tasks`;
  return (
    <span className="work-blocked" title={label} aria-label={label} role="img">
      <Icon name="warning" size={12} />
    </span>
  );
}

/** "3/5" for a task with a checklist: how many of its items are ticked. */
function ChecklistMark({ task }: { task: Task }) {
  if (!task.checklistTotal) return null;
  const label = `${task.checklistDone ?? 0} of ${task.checklistTotal} checklist items done`;
  return (
    <span className="work-checklist" title={label} aria-label={label} role="img">
      <Icon name="checklist" size={11} />
      {task.checklistDone ?? 0}/{task.checklistTotal}
    </span>
  );
}

/** A task's tags as small chips, capped so a heavily tagged task cannot take over its row. */
function TaskTags({ task, max = 2 }: { task: Task; max?: number }) {
  const tags = task.tags ?? [];
  if (tags.length === 0) return null;
  return (
    <>
      {tags.slice(0, max).map((tag) => (
        <TagChip key={tag.id} tag={tag} />
      ))}
      {tags.length > max && <span className="work-tags__more">+{tags.length - max}</span>}
    </>
  );
}

/**
 * The people on a task, as overlapping avatars.
 *
 * A task can have several owners, and a row or card has room for about three
 * faces — beyond that it becomes "+2" rather than shrinking every avatar until
 * none of them is recognisable. Falls back to the single hydrated
 * `assigneeName` when the owner array is missing.
 */
function AssigneeStack({ task, size = 22 }: { task: Task; size?: number }) {
  const owners = task.assignees ?? [];
  const MAX = 3;

  if (owners.length === 0) {
    if (task.assigneeName) {
      return <Avatar name={task.assigneeName} src={task.assigneeAvatarUrl ?? undefined} size={size} />;
    }
    return (
      <span className="work-row__unassigned" role="img" aria-label="Unassigned">
        <Icon name="people" size={14} />
      </span>
    );
  }

  const shown = owners.slice(0, MAX);
  const extra = owners.length - shown.length;

  return (
    <span
      className="work-avatars"
      aria-label={owners.length === 1 ? owners[0]!.displayName : `${owners.length} owners`}
      title={owners.map((o) => o.displayName).join(", ")}
    >
      {shown.map((owner) => (
        <Avatar key={owner.userId} name={owner.displayName} src={owner.avatarUrl ?? undefined} size={size} />
      ))}
      {extra > 0 && <span className="work-avatars__more">+{extra}</span>}
    </span>
  );
}

/* ─────────────────────────────── shared constants ─────────────────────────────── */

const PRIORITY_TONE: Record<Task["priority"], PillTone> = {
  low: "neutral",
  normal: "neutral",
  medium: "warn",
  high: "danger",
  urgent: "danger",
};

const BOARD_COLUMNS: { status: Task["status"]; label: string }[] = [
  { status: "todo", label: "To do" },
  { status: "in_progress", label: "In progress" },
  { status: "review", label: "Review" },
  { status: "done", label: "Done" },
];

/* ─────────────────────────────── board grouping ───────────────────────────────
 *
 * What the columns of the board mean.
 *
 * Status was the only answer for a long time, which made the board unable to
 * express the arrangement people most often want — Today / This Week / Later —
 * because a time horizon is not a status. Grouping is now a choice, and status
 * is one option among them.
 *
 * The important part is that a column is not just a filter: dropping a card
 * into one has to *mean* something, and what it means differs per grouping.
 * Dropping into "In progress" changes the status; dropping into "Today" changes
 * the due date. Each grouping therefore carries its own `apply`, and a grouping
 * with nothing sensible to apply says so, and its columns refuse drops rather
 * than silently doing nothing.
 *
 * Following Lark rather than Trello here: grouping is a lens over tasks that
 * have real fields, not a structure where the column *is* the state. That keeps
 * a task's meaning the same whoever is looking at it.
 */

type BoardGrouping = "status" | "due" | "assignee" | "priority";
const GROUPINGS = ["status", "due", "assignee", "priority"] as const;

type BoardColumn = {
  /** Stable key, used for React keys and drop targets. */
  id: string;
  label: string;
  /**
   * The real board_columns id when this is a project's custom column (as
   * opposed to a status/due/assignee lens). Present only for custom columns;
   * used to send moves as columnId and to drive column-management actions.
   */
  boardColumnId?: string;
  /** The completion category of a custom column, for a visual tint. */
  category?: BoardColumnDef["category"];
  /** Which tasks belong here. */
  contains: (task: Task) => boolean;
  /**
   * What dropping a card here changes, or null when the column cannot be a
   * drop target — "Unassigned" and "No date" are meaningful places for a task
   * to *be*, but dropping onto them is not a sentence anybody means.
   */
  apply: Partial<Pick<Task, "status" | "dueAt" | "assigneeId" | "priority" | "columnId">> | null;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

/** Due-date columns: the time horizons people actually plan in. */
function dueColumns(): BoardColumn[] {
  const today = startOfToday();
  const todayEnd = endOfDay(today);
  const weekEnd = endOfDay(new Date(today.getTime() + 6 * 86_400_000));

  const due = (task: Task) => (task.dueAt ? new Date(task.dueAt) : null);

  return [
    {
      id: "overdue",
      label: "Overdue",
      contains: (t) => {
        const d = due(t);
        return d !== null && d < today && t.status !== "done" && t.status !== "cancelled";
      },
      // Nothing to apply: you cannot make a task overdue by dragging it, and
      // pretending otherwise would mean back-dating somebody's deadline.
      apply: null,
    },
    {
      id: "today",
      label: "Today",
      contains: (t) => {
        const d = due(t);
        return d !== null && d >= today && d <= todayEnd;
      },
      apply: { dueAt: todayEnd.toISOString() },
    },
    {
      id: "week",
      label: "This week",
      contains: (t) => {
        const d = due(t);
        return d !== null && d > todayEnd && d <= weekEnd;
      },
      apply: { dueAt: weekEnd.toISOString() },
    },
    {
      id: "later",
      label: "Later",
      contains: (t) => {
        const d = due(t);
        return d !== null && d > weekEnd;
      },
      // A specific date would be a guess; "later" is deliberately vague, so
      // dropping here clears the deadline rather than inventing one.
      apply: { dueAt: null },
    },
    {
      id: "nodate",
      label: "No date",
      contains: (t) => t.dueAt === null || t.dueAt === undefined,
      apply: { dueAt: null },
    },
  ];
}

function boardColumnsFor(
  grouping: BoardGrouping,
  tasks: Task[],
  projectColumns: BoardColumnDef[] | null,
): BoardColumn[] {
  if (grouping === "due") return dueColumns();

  if (grouping === "priority") {
    return PRIORITY_OPTIONS.map((option) => ({
      id: option.value,
      label: option.label,
      contains: (t: Task) => t.priority === option.value,
      apply: { priority: option.value },
    }));
  }

  if (grouping === "assignee") {
    /*
     * Built from the tasks on screen rather than the whole directory: a column
     * per colleague who has no work here is noise, and a board of forty empty
     * columns is unusable.
     */
    const seen = new Map<string, string>();
    for (const task of tasks) {
      if (task.assigneeId) seen.set(task.assigneeId, task.assigneeName ?? "Someone");
    }
    const columns: BoardColumn[] = [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({
        id,
        label: name,
        contains: (t: Task) => t.assigneeId === id,
        apply: { assigneeId: id },
      }));
    columns.push({
      id: "unassigned",
      label: "Unassigned",
      contains: (t: Task) => !t.assigneeId,
      apply: { assigneeId: null },
    });
    return columns;
  }

  // Status grouping: a specific project's own custom columns when it has them,
  // else the fixed default columns (for "All projects", personal tasks, or a
  // project whose columns have not loaded yet).
  if (projectColumns && projectColumns.length > 0) {
    return projectColumns.map((col) => ({
      id: col.id,
      label: col.name,
      boardColumnId: col.id,
      category: col.category,
      contains: (t: Task) => t.columnId === col.id,
      apply: { columnId: col.id },
    }));
  }

  return BOARD_COLUMNS.map((col) => ({
    id: col.status,
    label: col.label,
    contains: (t: Task) => t.status === col.status,
    apply: { status: col.status },
  }));
}

const GROUPING_OPTIONS: { value: BoardGrouping; label: string }[] = [
  { value: "status", label: "Status" },
  { value: "due", label: "Due date" },
  { value: "assignee", label: "Assignee" },
  { value: "priority", label: "Priority" },
];

const STATUS_OPTIONS: { value: Task["status"]; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "review", label: "Review" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];

const PRIORITY_OPTIONS: { value: Task["priority"]; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

type ViewMode = "list" | "board" | "table" | "calendar" | "timeline";

/** Views that lay tasks out on a page of their own and want the whole result, not a first page. */
const WIDE_VIEWS: readonly ViewMode[] = ["table", "calendar", "timeline"];
const WIDE_LIMIT = 500;

function isOverdue(task: Task): boolean {
  if (!task.dueAt) return false;
  if (task.status === "done" || task.status === "cancelled") return false;
  return new Date(task.dueAt).getTime() < Date.now();
}

function formatDueDate(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: sameYear ? undefined : "numeric" });
}

/** Exports the flat task list underlying whichever view is on screen — board
 *  columns are a grouping of the same rows, not a different dataset. */
function exportTasksCsv(rows: Task[], extra?: ReturnType<typeof customCsvColumns>): void {
  const csv = toCsv(
    ["Title", "Status", "Priority", "Assignee(s)", "Project", "Due date", "Tags", ...(extra?.headers ?? [])],
    rows,
    (t) => [
      t.title,
      STATUS_OPTIONS.find((o) => o.value === t.status)?.label ?? t.status,
      t.priority,
      (t.assignees && t.assignees.length > 0 ? t.assignees.map((a) => a.displayName) : t.assigneeName ? [t.assigneeName] : []).join("; "),
      t.projectCode ?? "",
      t.dueAt ? formatDueDate(t.dueAt) : "",
      (t.tags ?? []).map((tag) => tag.name).join("; "),
      ...(extra?.cells(t) ?? []),
    ],
  );
  downloadCsv(`tasks-${new Date().toLocaleDateString("en-CA")}.csv`, csv);
}

/* ──────────────────────────────────── project sidebar ──────────────────────────────────── */

/** A saved grouping of tasks, which may span projects. */
type TaskListSummary = {
  id: string;
  name: string;
  ownerName: string | null;
  isOwner: boolean;
  itemCount: number;
};

interface ProjectSidebarProps {
  projects: Project[];
  loading: boolean;
  activeProjectId: string | undefined;
  onSelect: (projectId: string | undefined) => void;
  onCreateClick: () => void;
  lists: TaskListSummary[];
  activeListId: string | undefined;
  onSelectList: (listId: string | undefined) => void;
  onCreateList: () => void;
}

function ProjectSidebar({
  projects,
  loading,
  activeProjectId,
  onSelect,
  onCreateClick,
  lists,
  activeListId,
  onSelectList,
  onCreateList,
}: ProjectSidebarProps) {
  return (
    <aside className="work-sidebar" aria-label="Projects">
      <div className="work-sidebar__header">
        <h2>Work</h2>
        <span className="work-sidebar__header-actions">
          <LifecycleLauncher />
          <IconButton icon={<Icon name="plus" size={16} />} label="New project" size="sm" onClick={onCreateClick} />
        </span>
      </div>

      <div className="work-sidebar__scroll">
        <button
          type="button"
          className={`work-sidebar__all ${!activeProjectId ? "is-active" : ""}`}
          onClick={() => onSelect(undefined)}
          aria-current={!activeProjectId ? "true" : undefined}
        >
          <Icon name="work" size={15} />
          All projects
        </button>

        {loading ? (
          <div className="work-sidebar__loading">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="work-sidebar__skeleton-row">
                <Skeleton height={12} width="80%" />
                <Skeleton height={10} width="40%" />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <EmptyState
            icon="work"
            title="No projects yet"
            description="Create a project to start tracking tasks."
            action={
              <Button size="sm" variant="secondary" onClick={onCreateClick}>
                New project
              </Button>
            }
          />
        ) : (
          projects.map((project) => (
            /* Two actions on one row: the row filters the task list (the
               frequent action, kept one click away), and a separate control
               opens the project's own page. Making the whole row navigate
               would cost a click on every filter change. */
            <div
              key={project.id}
              className={`work-sidebar__row ${project.id === activeProjectId ? "is-active" : ""}`}
            >
              <button
                type="button"
                className="work-sidebar__item"
                onClick={() => onSelect(project.id)}
                aria-current={project.id === activeProjectId ? "true" : undefined}
              >
                <span className="work-sidebar__code">{project.code}</span>
                <span className="work-sidebar__name" title={project.name}>
                  {project.name}
                </span>
                {/* The bare number reads as meaningless out of context, so the
                    accessible name spells out what it counts. */}
                <span
                  className="work-sidebar__count"
                  aria-label={`${project.openTaskCount ?? 0} open ${project.openTaskCount === 1 ? "task" : "tasks"}`}
                >
                  {project.openTaskCount ?? 0}
                </span>
              </button>

              <Link
                to={`/work/project/${project.id}`}
                className="work-sidebar__open"
                aria-label={`Open ${project.name} details`}
                title="Project details"
              >
                <Icon name="arrow-right" size={13} />
              </Link>
            </div>
          ))
        )}

        {/* Lists sit beneath projects rather than replacing them: the project
            is where work lives, a list is a way of looking at it, and both
            need to be reachable from the same place. */}
        <div className="work-sidebar__section">
          <span className="work-sidebar__section-title">Lists</span>
          <IconButton
            icon={<Icon name="plus" size={14} />}
            label="New list"
            size="sm"
            onClick={onCreateList}
          />
        </div>

        {lists.length === 0 ? (
          <p className="work-sidebar__hint">Group tasks across projects.</p>
        ) : (
          lists.map((list) => (
            <button
              key={list.id}
              type="button"
              className={`work-sidebar__item ${list.id === activeListId ? "is-active" : ""}`}
              onClick={() => onSelectList(list.id)}
              aria-current={list.id === activeListId ? "true" : undefined}
            >
              <Icon name="pin" size={14} />
              <span className="work-sidebar__name" title={list.name}>
                {list.name}
              </span>
              <span
                className="work-sidebar__count"
                aria-label={`${list.itemCount} ${list.itemCount === 1 ? "task" : "tasks"}`}
              >
                {list.itemCount}
              </span>
            </button>
          ))
        )}
      </div>
    </aside>
  );
}

/* ──────────────────────────────────── new project form ──────────────────────────────────── */

interface NewProjectFormProps {
  onCreated: (project: Project) => void;
  onCancel: () => void;
}

/**
 * Derives a project code from its name.
 *
 * The code is a real requirement — it prefixes every task reference — but
 * asking someone to invent one before they have finished naming the thing is
 * busywork. This proposes one and stays out of the way once it is edited by
 * hand.
 */
function suggestCode(name: string, taken: Set<string>): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";

  const clean = (value: string) => value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

  const base =
    words.length === 1
      ? clean(words[0]!).slice(0, 5)
      : // Initials for a multi-word name: "Fleet Modernisation" → "FM", short
        // enough to stay readable in a dense task list.
        clean(words.map((w) => w[0]).join(""));

  if (base.length < 2) return "";
  if (!taken.has(base)) return base.slice(0, 12);

  /*
   * On a collision, lengthen before numbering: "Warehouse Automation" against
   * an existing WA becomes WAA (taking a second letter from the second word)
   * rather than WA2, because a code is read far more often than it is typed
   * and letters carry meaning that digits do not.
   */
  if (words.length > 1) {
    for (let extra = 2; extra <= 4; extra++) {
      const longer = clean(words.map((w) => w.slice(0, extra)).join(""));
      if (longer.length >= 2 && !taken.has(longer.slice(0, 12))) return longer.slice(0, 12);
    }
  }

  for (let n = 2; n < 100; n++) {
    const numbered = `${base.slice(0, 10)}${n}`;
    if (!taken.has(numbered)) return numbered;
  }
  return base.slice(0, 12);
}

function NewProjectForm({ onCreated, onCancel }: NewProjectFormProps) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [summary, setSummary] = useState("");
  const [health, setHealth] = useState("on_track");
  const [startDate, setStartDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [codeVisible, setCodeVisible] = useState(false);
  const [takenCodes, setTakenCodes] = useState<Set<string>>(new Set());
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [withChannel, setWithChannel] = useState(true);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [people, setPeople] = useState<{ id: string; displayName: string; jobTitle: string | null }[]>([]);
  const [saving, setSaving] = useState(false);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [dateError, setDateError] = useState<string | undefined>(undefined);

  /* The directory needs `user.list`, which not every role has. A failure here
   * is not an error worth surfacing — the section simply does not appear, and
   * members can still be added from the project page afterwards. */
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ people: { id: string; displayName: string; jobTitle: string | null }[] }>("/people")
      .then((res) => {
        if (!cancelled) setPeople(res.people);
      })
      .catch(() => undefined);

    /* Existing codes, so a suggestion is unique before it is submitted rather
     * than bouncing off a 409. The server still enforces it — this only saves
     * the round trip. */
    api
      .get<{ projects: { code: string }[] }>("/projects?archived=include")
      .then((res) => {
        if (!cancelled) setTakenCodes(new Set(res.projects.map((p) => p.code.toUpperCase())));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = useCallback(() => {
    const trimmedName = name.trim();
    const trimmedCode = code.trim();
    if (!trimmedName || !trimmedCode) return;

    // Checked here as well as server-side so the correction happens before a
    // round trip rather than after one.
    if (startDate && targetDate && targetDate < startDate) {
      setDateError("The target date is before the start date.");
      return;
    }

    setSaving(true);
    setCodeError(undefined);
    setDateError(undefined);
    // From a template, every date in it is re-based on the start date (today if left blank).
    const request = templateId
      ? api
          .post<{ project: { id: string; code: string; name: string } }>(`/project-templates/${templateId}/instantiate`, {
            name: trimmedName,
            code: trimmedCode,
            summary: summary.trim() || undefined,
            startDate: `${startDate || new Date().toISOString().slice(0, 10)}T00:00:00.000Z`,
          })
          // The list row carries the counts the sidebar needs; the instantiate reply does not.
          .then(async (res) => {
            const listed = await api
              .get<{ projects: Project[] }>("/projects")
              .then((r) => r.projects.find((p) => p.id === res.project.id))
              .catch(() => undefined);
            return { project: listed ?? (res.project as unknown as Project) };
          })
      : api.post<{ project: Project }>("/projects", {
        name: trimmedName,
        code: trimmedCode,
        summary: summary.trim() || undefined,
        health,
        // Sent as midnight UTC, not midnight local.
        //
        // `new Date("2026-08-01T00:00:00")` is local time, and anywhere east of
        // UTC that converts to 2026-07-31 — the stored date is a day earlier
        // than the one that was picked. A calendar date carries no timezone,
        // so it should not acquire one on the way to the server.
        startDate: startDate ? `${startDate}T00:00:00.000Z` : undefined,
        targetDate: targetDate ? `${targetDate}T00:00:00.000Z` : undefined,
        memberIds: memberIds.length ? memberIds : undefined,
      });

    request
      .then(async (res) => {
        /*
         * The channel is created after the project, in a second call, because
         * it needs the project's id. Its failure is reported but does not fail
         * the whole action — the project exists and is usable, and a channel
         * can be added later. Rolling the project back over this would be a
         * worse outcome than a project without a channel.
         */
        if (withChannel) {
          try {
            await api.post("/channels", {
              name: trimmedCode.toLowerCase(),
              kind: "project",
              projectId: res.project.id,
              topic: summary.trim() || undefined,
              memberIds,
            });
          } catch {
            toast("Project created, but its channel could not be set up.", { tone: "danger" });
          }
        }
        onCreated(res.project);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.code === "CODE_TAKEN") {
          setCodeError("That code is already in use.");
          return;
        }
        toast(error instanceof ApiError ? error.message : "Could not create project.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [name, code, summary, health, startDate, targetDate, memberIds, withChannel, templateId, onCreated, toast]);

  return (
    <form
      className="work-new-project"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <ProjectTemplateGallery value={templateId} onChange={setTemplateId} />

      <Input
        label="Project name"
        placeholder="Fleet Modernisation"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          // Stops proposing once the code has been edited by hand — quietly
          // overwriting someone's deliberate choice is worse than no help.
          if (!codeTouched) setCode(suggestCode(e.target.value, takenCodes));
        }}
        autoFocus
        required
      />

      {/* Generated from the name and shown as a fact, not a question. It is
          only a field when someone chooses to make it one — a code is
          required, but deciding it is not the author's problem. */}
      {codeVisible || codeError ? (
        <Input
          label="Code"
          hint="Prefixes every task in this project. 2–12 letters, numbers, or hyphens."
          value={code}
          onChange={(e) => {
            setCodeTouched(true);
            setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 12));
          }}
          error={codeError}
          autoFocus={codeVisible}
          required
        />
      ) : (
        <div className="work-new-project__code">
          <span className="work-new-project__label">Code</span>
          <span className="work-new-project__code-value">
            {code || <em>from the project name</em>}
          </span>
          <button
            type="button"
            className="work-new-project__code-edit"
            onClick={() => setCodeVisible(true)}
          >
            <Icon name="edit" size={12} />
            Change
          </button>
        </div>
      )}

      <Textarea
        label="Summary"
        hint="What is this project for? Optional, but the first thing someone new will read."
        placeholder="Retire the legacy telemetry stack and move every vehicle onto the new pipeline."
        value={summary}
        maxRows={4}
        onChange={(e) => setSummary(e.target.value)}
      />

      <div className="work-new-project__row">
        <Input
          label="Start date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Input
          label="Target date"
          type="date"
          value={targetDate}
          error={dateError}
          onChange={(e) => setTargetDate(e.target.value)}
        />
      </div>

      <Select label="Health" value={health} onChange={(e) => setHealth(e.target.value)}>
        <option value="on_track">On track</option>
        <option value="at_risk">At risk</option>
        <option value="off_track">Off track</option>
      </Select>

      {people.length > 0 && (
        <div className="work-new-project__members">
          <span className="work-new-project__label">
            Team <span>{memberIds.length ? `· ${memberIds.length} selected` : "· optional"}</span>
          </span>
          {/* Toggle chips rather than a multi-select: at this scale the whole
              directory fits on screen, and seeing who is already picked
              matters more than saving the vertical space. You are always the
              owner, so you are not listed as someone to add. */}
          <div className="work-new-project__people">
            {people
              .filter((person) => person.id !== viewer?.id)
              .map((person) => {
                const selected = memberIds.includes(person.id);
                return (
                  <button
                    key={person.id}
                    type="button"
                    className={`work-person-chip ${selected ? "is-selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() =>
                      setMemberIds((prev) =>
                        prev.includes(person.id) ? prev.filter((id) => id !== person.id) : [...prev, person.id],
                      )
                    }
                  >
                    <Avatar name={person.displayName} size={20} />
                    <span>{person.displayName}</span>
                    {selected && <Icon name="check" size={12} strokeWidth={3} />}
                  </button>
                );
              })}
          </div>
          <span className="work-new-project__hint">
            You are the owner. Anyone added here can work on the project's tasks from the start.
          </span>
        </div>
      )}

      <div className="work-new-project__channel">
        <Checkbox
          label={`Create a #${code.toLowerCase() || "project"} chat channel`}
          checked={withChannel}
          onChange={(e) => setWithChannel(e.target.checked)}
        />
        <span className="work-new-project__hint">
          Chat and work stay together — messages in this channel can be turned into tasks on this project.
        </span>
      </div>

      <div className="work-new-project__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!name.trim() || !code.trim()}>
          Create project
        </Button>
      </div>
    </form>
  );
}

/* ──────────────────────────────────── filter bar ──────────────────────────────────── */

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  priority: string;
  onPriorityChange: (value: string) => void;
  assignedToMe: boolean;
  onAssignedToMeChange: (value: boolean) => void;
  overdueOnly: boolean;
  onOverdueChange: (value: boolean) => void;
  tags: Tag[];
  tagId: string;
  onTagChange: (value: string) => void;
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  /** Rendered after the view switch (the saved-views menu). */
  extra?: ReactNode;
  grouping: BoardGrouping;
  onGroupingChange: (value: BoardGrouping) => void;
}

function FilterBar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  priority,
  onPriorityChange,
  assignedToMe,
  onAssignedToMeChange,
  overdueOnly,
  onOverdueChange,
  tags,
  tagId,
  onTagChange,
  view,
  onViewChange,
  extra,
  grouping,
  onGroupingChange,
}: FilterBarProps) {
  // Local echo of `search` so typing feels instant while the debounced value
  // (owned by the parent) is what actually drives the fetch.
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => setSearchDraft(search), [search]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  return (
    <div className="work-filterbar">
      <label className="work-filterbar__search">
        <Icon name="search" size={14} />
        <input
          type="search"
          placeholder="Search tasks…"
          value={searchDraft}
          onChange={(e) => {
            const value = e.target.value;
            setSearchDraft(value);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => onSearchChange(value), 250);
          }}
          aria-label="Search tasks"
        />
      </label>

      <Select
        aria-label="Filter by status"
        value={status}
        onChange={(e) => onStatusChange(e.target.value)}
        className="work-filterbar__select"
      >
        <option value="">Any status</option>
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      {tags.length > 0 && (
        <Select
          aria-label="Filter by tag"
          value={tagId}
          onChange={(e) => onTagChange(e.target.value)}
          className="work-filterbar__select"
        >
          <option value="">Any tag</option>
          {tags.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name}
            </option>
          ))}
        </Select>
      )}

      <Select
        aria-label="Filter by priority"
        value={priority}
        onChange={(e) => onPriorityChange(e.target.value)}
        className="work-filterbar__select"
      >
        <option value="">Any priority</option>
        {PRIORITY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      <Checkbox
        label="Assigned to me"
        checked={assignedToMe}
        onChange={(e) => onAssignedToMeChange(e.target.checked)}
      />

      {/* Visible and clearable, because Insights links straight into it:
          arriving at a filtered list with no sign of why it is filtered reads
          as half the work having vanished. */}
      <Checkbox
        label="Overdue"
        checked={overdueOnly}
        onChange={(e) => onOverdueChange(e.target.checked)}
      />

      <div className="work-filterbar__spacer" />

      {/* Only in board view: grouping is what the columns mean, and the list
          has no columns to mean anything. */}
      {view === "board" && (
        <label className="work-filterbar__group">
          <span>Group by</span>
          <Select
            aria-label="Group board by"
            value={grouping}
            onChange={(e) => onGroupingChange(e.target.value as BoardGrouping)}
          >
            {GROUPING_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>
      )}

      <SegmentedControl
        aria-label="View"
        value={view}
        onChange={onViewChange}
        options={[
          { value: "list", label: "List" },
          { value: "board", label: "Board" },
          { value: "table", label: "Table" },
          { value: "calendar", label: "Calendar" },
          { value: "timeline", label: "Timeline" },
        ]}
      />
      {extra}
    </div>
  );
}

/* ──────────────────────────────────── inline add-task ──────────────────────────────────── */

interface InlineAddTaskProps {
  placeholder: string;
  onAdd: (title: string) => void;
  /** Hands the half-typed title to the full dialog instead of creating it. */
  onExpand: (title: string) => void;
  className?: string;
}

function InlineAddTask({ placeholder, onAdd, onExpand, className }: InlineAddTaskProps) {
  const [active, setActive] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  /* Set while the pointer is going down on the expand button, so the input's
   * blur handler knows not to create a bare task out from under it. */
  const expandingRef = useRef(false);

  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed) onAdd(trimmed);
    setValue("");
  };

  if (!active) {
    return (
      <button type="button" className={`work-add-task work-add-task--idle ${className ?? ""}`} onClick={() => setActive(true)}>
        <Icon name="plus" size={14} />
        Add task
      </button>
    );
  }

  return (
    <div className={`work-add-task__wrap ${className ?? ""}`}>
      <input
        ref={inputRef}
        className="work-add-task work-add-task--active"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (expandingRef.current) return;
          commit();
          setActive(false);
        }}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            // Stay open so several tasks can be added back-to-back.
            inputRef.current?.focus();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setValue("");
            setActive(false);
          }
        }}
      />
      {/* Pointer-down rather than click: blur fires first otherwise, and the
          bare task would already have been created. */}
      <button
        type="button"
        className="work-add-task__expand"
        title="Add a brief, assignee, and files"
        onPointerDown={() => {
          expandingRef.current = true;
        }}
        onClick={() => {
          const draft = value.trim();
          setValue("");
          setActive(false);
          expandingRef.current = false;
          onExpand(draft);
        }}
      >
        <Icon name="edit" size={13} />
        Add detail
      </button>
    </div>
  );
}

/* ──────────────────────────────────── move-to menu (keyboard fallback for DnD) ──────────────────────────────────── */

interface MoveToMenuProps {
  task: Task;
  onMove: (status: Task["status"]) => void;
  onDelete: (task: Task) => void;
}

function MoveToMenu({ task, onMove, onDelete }: MoveToMenuProps) {
  const [open, setOpen] = useState(false);
  const { viewer } = useAuth();
  // Hide the delete item unless this viewer may actually delete this task
  // (admin, creator, or assigner). The API enforces the same rule.
  const canDelete = viewer ? canDeleteTask(task, viewer) : false;

  const items: MenuItem[] = [
    ...BOARD_COLUMNS.map((col) => ({
      id: col.status,
      label: col.label,
      // The column it is already in stays visible but unselectable: removing
      // it would shuffle the list and cost the reader their place.
      disabled: col.status === task.status,
      onSelect: () => onMove(col.status),
    })),
    // Delete only appears for someone who may actually delete this task (admin,
    // creator, or assigner). The API enforces the same rule; hiding it here just
    // avoids offering an action that would be refused.
    ...(canDelete
      ? [{ id: "delete", label: "Delete task", icon: "trash" as const, destructive: true, onSelect: () => onDelete(task) }]
      : []),
  ];

  return (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      label="More actions"
      items={items}
    >
      <IconButton
        icon={<Icon name="more" size={14} />}
        label={`More actions for ${task.title}`}
        size="sm"
        onClick={() => setOpen((v) => !v)}
      />
    </Menu>
  );
}

/* ──────────────────────────────────── add-to-list menu ──────────────────────────────────── */

interface AddToListMenuProps {
  task: Task;
  lists: TaskListSummary[];
  onAdd: (task: Task, listId: string) => void;
  onCreateAndAdd: (task: Task) => void;
}

/**
 * A second small trigger beside "Move to" rather than folded into it: `Menu`
 * has no submenu, and a list of workflow columns interleaved with a
 * (possibly long, personal) list of saved lists in one flat menu would read
 * as one confused set of options rather than two different questions
 * ("what state is this in" vs "what am I tracking it under").
 */
function AddToListMenu({ task, lists, onAdd, onCreateAndAdd }: AddToListMenuProps) {
  const [open, setOpen] = useState(false);

  const items: MenuItem[] =
    lists.length === 0
      ? [{ id: "new", label: "New list…", icon: "plus", onSelect: () => onCreateAndAdd(task) }]
      : [
          ...lists.map((list) => ({
            id: list.id,
            label: list.name,
            onSelect: () => onAdd(task, list.id),
          })),
          { id: "new", label: "New list…", icon: "plus", onSelect: () => onCreateAndAdd(task) },
        ];

  return (
    <Menu open={open} onClose={() => setOpen(false)} label="Add to list" items={items}>
      <IconButton
        icon={<Icon name="checklist" size={14} />}
        label={`Add ${task.title} to a list`}
        size="sm"
        onClick={() => setOpen((v) => !v)}
      />
    </Menu>
  );
}

/* ──────────────────────────────────── list view ──────────────────────────────────── */

interface TaskListRowProps {
  task: Task;
  showProjectCode: boolean;
  onToggleDone: (task: Task) => void;
  onRename: (task: Task, title: string) => void;
  onMove: (task: Task, status: Task["status"]) => void;
  onDelete: (task: Task) => void;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: (task: Task) => void;
  lists: TaskListSummary[];
  onAddToList: (task: Task, listId: string) => void;
  onCreateListAndAdd: (task: Task) => void;
}

function TaskListRow({
  task,
  showProjectCode,
  onToggleDone,
  onRename,
  onMove,
  onDelete,
  selectionMode,
  selected,
  onToggleSelected,
  lists,
  onAddToList,
  onCreateListAndAdd,
}: TaskListRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== task.title) onRename(task, trimmed);
    else setDraft(task.title);
    setEditing(false);
  };

  const overdue = isOverdue(task);

  return (
    <div className={`work-row ${task.status === "done" ? "is-done" : ""}`}>
      {selectionMode && (
        <span className="work-row__select">
          <Checkbox
            checked={selected}
            onChange={() => onToggleSelected(task)}
            aria-label={`Select ${task.title}`}
          />
        </span>
      )}
      <Checkbox
        checked={task.status === "done"}
        onChange={() => onToggleDone(task)}
        aria-label={task.status === "done" ? "Mark as not done" : "Mark as done"}
      />

      {editing ? (
        <input
          ref={inputRef}
          className="work-row__title-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(task.title);
              setEditing(false);
            }
          }}
        />
      ) : (
        // Titled because the column is fixed-width and a long task name
        // ellipsises — otherwise the only way to read it is to open the task.
        <button type="button" className="work-row__title" title={task.title} onClick={() => setEditing(true)}>
          {!!task.openBlockerCount && <BlockedMark count={task.openBlockerCount} />}
          {task.title}
          <ChecklistMark task={task} />
          <TaskTags task={task} />
        </button>
      )}

      {/* The cell is always rendered, even when there is nothing to put in it.
          Omitting it shifted every following column left on that row alone,
          which is why the pills and dates never lined up between rows. */}
      {showProjectCode && (
        <span className="work-row__project">
          {task.projectCode ? <Pill tone="neutral">{task.projectCode}</Pill> : null}
        </span>
      )}

      <span className="work-row__priority">
        <Pill tone={PRIORITY_TONE[task.priority]}>{task.priority}</Pill>
      </span>

      <AssigneeStack task={task} size={22} />

      <span className={`work-row__due ${overdue ? "is-overdue" : ""}`}>
        {task.dueAt ? formatDueDate(task.dueAt) : ""}
      </span>

      {/* A separate control rather than making the title a link: clicking the
          title starts an inline rename, which is the far more frequent action
          in a dense list. Overloading one target with both would make each
          less predictable. */}
      <Link
        to={`/work/task/${task.id}`}
        className="work-row__open"
        aria-label={`Open ${task.title}`}
        title="Open task"
      >
        <Icon name="arrow-right" size={14} />
      </Link>

      <AddToListMenu task={task} lists={lists} onAdd={onAddToList} onCreateAndAdd={onCreateListAndAdd} />
      <MoveToMenu task={task} onMove={(status) => onMove(task, status)} onDelete={onDelete} />
    </div>
  );
}

interface ListViewProps {
  tasks: Task[];
  showProjectCode: boolean;
  onToggleDone: (task: Task) => void;
  onRename: (task: Task, title: string) => void;
  onMove: (task: Task, status: Task["status"]) => void;
  onDelete: (task: Task) => void;
  onAddTask: (title: string) => void;
  onExpandTask: (title: string) => void;
  selectionMode: boolean;
  selectedIds: Set<string>;
  onToggleSelected: (task: Task) => void;
  onToggleSelectAll: () => void;
  lists: TaskListSummary[];
  onAddToList: (task: Task, listId: string) => void;
  onCreateListAndAdd: (task: Task) => void;
  /** Present when a project is selected: the list is then grouped by its sections. */
  sectionControls?: SectionControls;
}

/** Everything the list needs to show and edit a project's sections. */
interface SectionControls {
  sections: ProjectSection[];
  canManage: boolean;
  onCreate: (name: string) => void;
  onRename: (section: ProjectSection, name: string) => void;
  onMove: (section: ProjectSection, by: -1 | 1) => void;
  onDelete: (section: ProjectSection) => void;
  onAddTask: (section: ProjectSection, title: string) => void;
}

/** A section's title row: its name, how many tasks it holds, and (for editors) rename, reorder, delete. */
function SectionHeader({
  section,
  count,
  index,
  total,
  controls,
}: {
  section: ProjectSection;
  count: number;
  index: number;
  total: number;
  controls: SectionControls;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.name);
  const commit = () => {
    const name = draft.trim();
    setEditing(false);
    if (name && name !== section.name) controls.onRename(section, name);
    else setDraft(section.name);
  };
  return (
    <div className="work-section-head">
      {editing ? (
        <input
          className="work-section-head__input"
          aria-label="Section name"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(section.name);
              setEditing(false);
            }
          }}
        />
      ) : (
        <span className="work-section-head__name">{section.name}</span>
      )}
      <span className="work-section-head__count">{count}</span>
      {controls.canManage && !editing && (
        <span className="work-section-head__actions">
          <IconButton icon={<Icon name="edit" size={13} />} label={`Rename ${section.name}`} size="sm" onClick={() => setEditing(true)} />
          <IconButton icon={<Icon name="chevron-up" size={13} />} label={`Move ${section.name} up`} size="sm" disabled={index === 0} onClick={() => controls.onMove(section, -1)} />
          <IconButton icon={<Icon name="chevron-down" size={13} />} label={`Move ${section.name} down`} size="sm" disabled={index === total - 1} onClick={() => controls.onMove(section, 1)} />
          <IconButton icon={<Icon name="trash" size={13} />} label={`Delete ${section.name}`} size="sm" onClick={() => controls.onDelete(section)} />
        </span>
      )}
    </div>
  );
}

function ListView({
  sectionControls,
  tasks,
  showProjectCode,
  onToggleDone,
  onRename,
  onMove,
  onDelete,
  onAddTask,
  onExpandTask,
  selectionMode,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
  lists,
  onAddToList,
  onCreateListAndAdd,
}: ListViewProps) {
  const allSelected = tasks.length > 0 && tasks.every((t) => selectedIds.has(t.id));

  const renderRow = (task: Task) => (
    <TaskListRow
      key={task.id}
      task={task}
      showProjectCode={showProjectCode}
      onToggleDone={onToggleDone}
      onRename={onRename}
      onMove={onMove}
      onDelete={onDelete}
      selectionMode={selectionMode}
      selected={selectedIds.has(task.id)}
      onToggleSelected={onToggleSelected}
      lists={lists}
      onAddToList={onAddToList}
      onCreateListAndAdd={onCreateListAndAdd}
    />
  );

  /* Tasks keep their order within a group. Without sections there is one
     unnamed group and the list reads exactly as it always did; with them, the
     tasks that belong to none follow the named sections under no heading
     until a section exists to be compared against. */
  const sections = sectionControls?.sections ?? [];
  const groups: { section: ProjectSection | null; index: number; tasks: Task[] }[] = [];
  if (sections.length === 0) {
    groups.push({ section: null, index: 0, tasks });
  } else {
    const known = new Set(sections.map((s) => s.id));
    sections.forEach((section, index) => {
      groups.push({ section, index, tasks: tasks.filter((t) => t.sectionId === section.id) });
    });
    const loose = tasks.filter((t) => !t.sectionId || !known.has(t.sectionId));
    if (loose.length > 0) groups.push({ section: null, index: sections.length, tasks: loose });
  }

  return (
    <div className={`work-list ${showProjectCode ? "" : "work-list--no-project"} ${selectionMode ? "work-list--selecting" : ""}`}>
      {/* Same grid as the rows, so a label sits over the column it names. The
          empty cells are the done-checkbox, avatar, open and menu columns,
          which are self-explanatory and would only add noise if labelled.
          No longer `aria-hidden`: the select-all checkbox is a real control,
          and hiding its container would hide it from assistive tech too.
          The select column itself only exists while selectionMode is on —
          see work-list--selecting in work.css for the matching grid change. */}
      <div className="work-row work-row--head">
        {selectionMode && (
          <span className="work-row__select">
            <Checkbox checked={allSelected} onChange={onToggleSelectAll} aria-label={`Select all ${tasks.length} tasks`} />
          </span>
        )}
        <span aria-hidden="true" />
        <span>Task</span>
        {showProjectCode && <span>Project</span>}
        <span>Priority</span>
        <span>Assignee</span>
        <span>Due</span>
        <span />
        <span />
        <span />
      </div>
      {groups.map((group) => (
        <div key={group.section?.id ?? "none"} className="work-section">
          {group.section && sectionControls && (
            <SectionHeader
              section={group.section}
              count={group.tasks.length}
              index={group.index}
              total={sectionControls.sections.length}
              controls={sectionControls}
            />
          )}
          {group.tasks.map((task) => renderRow(task))}
          {group.section && sectionControls?.canManage && (
            <InlineAddTask
              placeholder={`Add to ${group.section.name}…`}
              onAdd={(title) => sectionControls.onAddTask(group.section!, title)}
              onExpand={onExpandTask}
              className="work-list__add"
            />
          )}
        </div>
      ))}
      {sectionControls?.canManage && (
        <InlineAddTask
          placeholder="New section name, press Enter…"
          onAdd={sectionControls.onCreate}
          onExpand={sectionControls.onCreate}
          className="work-list__add work-list__add-section"
        />
      )}
      <InlineAddTask
        placeholder="Task title, press Enter…"
        onAdd={onAddTask}
        onExpand={onExpandTask}
        className="work-list__add"
      />
    </div>
  );
}

/* ──────────────────────────────────── board view ──────────────────────────────────── */

interface DragState {
  taskId: string;
  fromStatus: Task["status"];
}

interface BoardCardProps {
  task: Task;
  showProjectCode: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>, task: Task) => void;
  onDragEnd: () => void;
  onMove: (task: Task, status: Task["status"]) => void;
  onDelete: (task: Task) => void;
  isDragging: boolean;
  lists: TaskListSummary[];
  onAddToList: (task: Task, listId: string) => void;
  onCreateListAndAdd: (task: Task) => void;
}

function BoardCard({
  task,
  showProjectCode,
  onDragStart,
  onDragEnd,
  onMove,
  onDelete,
  isDragging,
  lists,
  onAddToList,
  onCreateListAndAdd,
}: BoardCardProps) {
  const overdue = isOverdue(task);
  const navigate = useNavigate();
  return (
    <div
      className={`work-card ${isDragging ? "is-dragging" : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onDragEnd={onDragEnd}
      /* The card carries the drag cursor, but before this the only way to
         open a task from the board was the small title link — everywhere
         else on the card (padding, meta row, footer) looked clickable and
         did nothing. A `click` never fires after an actual drag gesture, so
         opening on click here doesn't fight the existing drag-and-drop. */
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button")) return;
        navigate(`/work/task/${task.id}`);
      }}
      tabIndex={0}
      role="article"
      aria-label={task.title}
    >
      <div className="work-card__top">
        {/* draggable={false} on the link: a nested anchor is draggable by
            default and would start its own link-drag instead of the card's. */}
        <Link to={`/work/task/${task.id}`} className="work-card__title" draggable={false}>
          {task.title}
        </Link>
        <AddToListMenu task={task} lists={lists} onAdd={onAddToList} onCreateAndAdd={onCreateListAndAdd} />
        <MoveToMenu task={task} onMove={(status) => onMove(task, status)} onDelete={onDelete} />
      </div>
      <div className="work-card__meta">
        {showProjectCode && task.projectCode && (
          <Pill tone="neutral" title={task.projectCode}>
            {task.projectCode}
          </Pill>
        )}
        <Pill tone={PRIORITY_TONE[task.priority]}>{task.priority}</Pill>
        {task.dueAt && <span className={`work-card__due ${overdue ? "is-overdue" : ""}`}>{formatDueDate(task.dueAt)}</span>}
        {/* A repeating task looks like any other until it is completed and
            reappears, so it says so up front. */}
        {!!task.openBlockerCount && <BlockedMark count={task.openBlockerCount} />}
        <TaskTags task={task} />
        {task.recurrenceRule && (
          <span className="work-card__repeats" title="Repeats — completing this creates the next one">
            <Icon name="repeat" size={11} />
          </span>
        )}
      </div>
      <div className="work-card__foot">
        <AssigneeStack task={task} size={20} />
        <ChecklistMark task={task} />
        {!!task.subtaskCount && (
          <span className="work-card__subtasks">
            <Icon name="check" size={11} />
            {task.completedSubtaskCount}/{task.subtaskCount}
          </span>
        )}
        {!!task.commentCount && (
          <span className="work-card__comments">
            <Icon name="chat" size={11} />
            {task.commentCount}
          </span>
        )}
      </div>
    </div>
  );
}

/** Rename/delete menu on a custom board column's header. */
function ColumnHeaderMenu({
  columnName,
  onRename,
  onDelete,
}: {
  columnId: string;
  columnName: string;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const items: MenuItem[] = [
    {
      id: "rename",
      label: "Rename column",
      icon: "edit",
      onSelect: () => {
        const name = window.prompt("Rename column:", columnName)?.trim();
        if (name && name !== columnName) onRename(name);
      },
    },
    { id: "delete", label: "Delete column", icon: "trash", destructive: true, onSelect: onDelete },
  ];
  return (
    <Menu open={open} onClose={() => setOpen(false)} label={`Manage the ${columnName} column`} items={items}>
      <IconButton
        icon={<Icon name="more" size={13} />}
        label={`Manage the ${columnName} column`}
        size="sm"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
      />
    </Menu>
  );
}

/** "Add column" affordance: prompts for a name and which category it belongs to. */
function AddColumnButton({ onAdd }: { onAdd: (name: string, category: BoardColumnDef["category"]) => void }) {
  return (
    <button
      type="button"
      className="work-board__add-column-btn"
      onClick={() => {
        const name = window.prompt("New column name:")?.trim();
        if (!name) return;
        // Which category the column belongs to decides its completion meaning.
        const raw = window
          .prompt("Category — type: todo, in_progress, or done", "todo")
          ?.trim()
          .toLowerCase();
        const category: BoardColumnDef["category"] =
          raw === "done" ? "done" : raw === "in_progress" ? "in_progress" : "todo";
        onAdd(name, category);
      }}
    >
      <Icon name="plus" size={14} />
      Add column
    </button>
  );
}

interface BoardViewProps {
  tasks: Task[];
  showProjectCode: boolean;
  grouping: BoardGrouping;
  /** The selected project's custom columns, or null for "All projects"/none. */
  projectColumns: BoardColumnDef[] | null;
  /** Whether the viewer may add/rename/delete columns on this project's board. */
  canManageColumns: boolean;
  onAddColumn: (name: string, category: BoardColumnDef["category"]) => void;
  onRenameColumn: (columnId: string, name: string) => void;
  onDeleteColumn: (columnId: string, name: string) => void;
  /**
   * Applies whatever the destination column means, plus the new ordering.
   * `changes` is null when the column is not a drop target.
   */
  onDropInColumn: (
    taskId: string,
    changes: BoardColumn["apply"],
    afterTaskId: string | null,
    beforeTaskId: string | null,
  ) => void;
  onMove: (task: Task, status: Task["status"]) => void;
  onDelete: (task: Task) => void;
  onAddTask: (columnApply: BoardColumn["apply"], title: string) => void;
  onExpandTask: (columnApply: BoardColumn["apply"], title: string) => void;
  lists: TaskListSummary[];
  onAddToList: (task: Task, listId: string) => void;
  onCreateListAndAdd: (task: Task) => void;
}

function BoardView({
  tasks,
  showProjectCode,
  grouping,
  projectColumns,
  canManageColumns,
  onAddColumn,
  onRenameColumn,
  onDeleteColumn,
  onDropInColumn,
  onMove,
  onDelete,
  onAddTask,
  onExpandTask,
  lists,
  onAddToList,
  onCreateListAndAdd,
}: BoardViewProps) {
  const [dragging, setDragging] = useState<DragState | null>(null);
  const [dropTarget, setDropTarget] = useState<{ columnId: string; index: number } | null>(null);

  const columns = useMemo(
    () => boardColumnsFor(grouping, tasks, projectColumns),
    [grouping, tasks, projectColumns],
  );

  const byColumn = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const col of columns) map.set(col.id, []);
    for (const task of tasks) {
      /*
       * Cancelled work is hidden from the board but not from the list.
       *
       * A board is for work in flight; a cancelled card occupies a column
       * forever without ever moving. It stays visible in the list view, which
       * is the honest record of everything.
       */
      if (task.status === "cancelled") continue;
      // First match wins, so overlapping definitions cannot double-count a card.
      const column = columns.find((col) => col.contains(task));
      if (column) map.get(column.id)?.push(task);
    }
    return map;
  }, [tasks, columns]);

  const handleDragStart = (e: DragEvent<HTMLDivElement>, task: Task) => {
    setDragging({ taskId: task.id, fromStatus: task.status });
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set for the drag to initiate at all.
    e.dataTransfer.setData("text/plain", task.id);
  };

  const handleDragEnd = () => {
    setDragging(null);
    setDropTarget(null);
  };

  const handleColumnDragOver = (e: DragEvent<HTMLDivElement>, column: BoardColumn, index: number) => {
    // A column with nothing to apply cannot accept a drop — "Overdue" is a
    // description, not somewhere you can put something.
    if (!column.apply) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget({ columnId: column.id, index });
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, column: BoardColumn) => {
    e.preventDefault();
    if (!dragging || !column.apply) return;
    const cards = byColumn.get(column.id) ?? [];
    const dropIndex = dropTarget?.columnId === column.id ? dropTarget.index : cards.length;

    // Exclude the dragged card itself from the target column before indexing,
    // so dropping it back near its own old slot doesn't shift the count.
    const withoutDragged = cards.filter((t) => t.id !== dragging.taskId);
    const clampedIndex = Math.max(0, Math.min(dropIndex, withoutDragged.length));
    const before = withoutDragged[clampedIndex] ?? null;
    const after = clampedIndex > 0 ? withoutDragged[clampedIndex - 1] : null;

    onDropInColumn(dragging.taskId, column.apply, after?.id ?? null, before?.id ?? null);
    setDragging(null);
    setDropTarget(null);
  };

  return (
    <div className="work-board">
      {columns.map((col) => {
        const columnTasks = byColumn.get(col.id) ?? [];
        return (
          <div
            key={col.id}
            className={`work-board__column ${dropTarget?.columnId === col.id ? "is-drop-target" : ""} ${
              col.apply ? "" : "is-readonly"
            }`}
            onDragOver={(e) => handleColumnDragOver(e, col, columnTasks.length)}
            onDrop={(e) => handleDrop(e, col)}
          >
            <div className={`work-board__header ${col.category ? `is-${col.category}` : ""}`}>
              <span>{col.label}</span>
              <span className="work-board__count">{columnTasks.length}</span>
              {/* Column management only for a project's real custom columns
                  (not the status/due/assignee lenses), and only for someone who
                  may manage the board. */}
              {col.boardColumnId && canManageColumns && (
                <ColumnHeaderMenu
                  columnId={col.boardColumnId}
                  columnName={col.label}
                  onRename={(name) => onRenameColumn(col.boardColumnId!, name)}
                  onDelete={() => onDeleteColumn(col.boardColumnId!, col.label)}
                />
              )}
            </div>

            <div className="work-board__cards">
              {columnTasks.map((task, index) => (
                <div key={task.id}>
                  {dropTarget?.columnId === col.id && dropTarget.index === index && dragging && (
                    <div className="work-board__drop-indicator" />
                  )}
                  <div
                    onDragOver={(e) => {
                      e.stopPropagation();
                      handleColumnDragOver(e, col, index);
                    }}
                  >
                    <BoardCard
                      task={task}
                      showProjectCode={showProjectCode}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onMove={onMove}
                      onDelete={onDelete}
                      isDragging={dragging?.taskId === task.id}
                      lists={lists}
                      onAddToList={onAddToList}
                      onCreateListAndAdd={onCreateListAndAdd}
                    />
                  </div>
                </div>
              ))}
              {dropTarget?.columnId === col.id && dropTarget.index === columnTasks.length && dragging && (
                <div className="work-board__drop-indicator" />
              )}
            </div>

            {/* A column you cannot drop into is one you cannot create into
                either: there is no defensible value to give the new task. */}
            {col.apply && (
              <InlineAddTask
                placeholder="Task title, press Enter…"
                onAdd={(title) => onAddTask(col.apply, title)}
                onExpand={(title) => onExpandTask(col.apply, title)}
                className="work-board__add"
              />
            )}
          </div>
        );
      })}

      {/* Add a column — only on a project board (status grouping with real
          custom columns) and only for someone who may manage it. */}
      {grouping === "status" && projectColumns && canManageColumns && (
        <div className="work-board__add-column">
          <AddColumnButton onAdd={onAddColumn} />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────── main view ──────────────────────────────────── */

interface WorkProps {
  /**
   * The `/tasks` entry point: "what's assigned to me, across every project",
   * with no project browser — Lark's separate Tasks app, rather than a filter
   * buried inside Work. Same screen and data underneath; `mine=1` is just
   * defaulted instead of requiring a click, and the project sidebar has
   * nothing to add to a view that is deliberately not scoped to one project.
   */
  personalOnly?: boolean;
}

export function Work({ personalOnly = false }: WorkProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewer } = useAuth();
  const { toast } = useToast();

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [lists, setLists] = useState<TaskListSummary[]>([]);
  const [creatingProject, setCreatingProject] = useState(false);
  /* Below 900px the project sidebar has nowhere to sit beside the task list —
   * there is only one column — so it opens as a sheet instead, the same
   * pattern the shell's own mobile "More" menu uses. */
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  /* Bulk selection — list view only; the board's cards are dragged one at a
     time and have no row to check. Cleared whenever the task set reloads, the
     same rule Admin's and Files' tables follow.

     The checkbox column only exists in the DOM while selectionMode is on —
     otherwise every row carries a control nobody uses on the common path of
     "open one task." Turning selection mode off also clears any selection,
     since a hidden "3 selected" toolbar with no visible checkboxes to explain
     it would be confusing. */
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [bulkReassigning, setBulkReassigning] = useState(false);
  const [bulkReassignBusy, setBulkReassignBusy] = useState(false);
  const [bulkClosing, setBulkClosing] = useState(false);
  const [bulkCloseBusy, setBulkCloseBusy] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  // The single task queued for deletion from a row's ··· menu (null when the
  // confirm dialog is closed), and whether its delete request is in flight.
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);
  const [deleteTaskBusy, setDeleteTaskBusy] = useState(false);
  const [bulkAssignees, setBulkAssignees] = useState<{ id: string; displayName: string }[] | null>(null);
  const [bulkAddingToList, setBulkAddingToList] = useState(false);

  /* The full dialog. `detailDraft` carries a title typed into an inline row
   * that the user then chose to expand, so nothing they typed is lost. */
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailStatus, setDetailStatus] = useState<Task["status"]>("todo");
  const [detailDraft, setDetailDraft] = useState("");

  // URL is the single source of truth for view + filters, so a reload or a
  // shared link reproduces exactly what was on screen.
  const projectId = searchParams.get("project") ?? undefined;
  const listId = searchParams.get("list") ?? undefined;
  /*
   * A per-person view preference, held in the URL so a reload or a shared link
   * reproduces what was on screen. It changes how *you* see the board and not
   * what anybody else sees — Lark's model, where grouping is a lens rather than
   * a structure, which is what keeps two people from disagreeing about where a
   * card lives.
   */
  // View and grouping are remembered per browser as well as reflected in the
  // URL: the URL stays shareable, but returning to Work later (with no query
  // string) reopens the layout and grouping last used rather than snapping back
  // to a List / by-Status default. The URL wins when it names a value.
  const [rememberedView, rememberView] = usePersistentState<ViewMode>("work.view", "list", [
    "list",
    "board",
    "table",
    "calendar",
    "timeline",
  ]);
  const [rememberedGroup, rememberGroup] = usePersistentState<BoardGrouping>(
    "work.group",
    "status",
    GROUPINGS,
  );
  const urlView = searchParams.get("view");
  const view = (
    urlView === "board" || urlView === "list" || urlView === "table" || urlView === "calendar" || urlView === "timeline"
      ? urlView
      : rememberedView
  ) as ViewMode;
  const wide = (WIDE_VIEWS as readonly string[]).includes(view);
  const urlGroup = searchParams.get("group");
  const grouping = (urlGroup && (GROUPINGS as readonly string[]).includes(urlGroup) ? urlGroup : rememberedGroup) as BoardGrouping;
  const search = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "";
  const priority = searchParams.get("priority") ?? "";
  const assignedToMe = searchParams.get("mine") === "1";
  // One tag id from the office's tags; the server filters on it (any-of, so a
  // comma list would also work, but the bar offers one at a time).
  const tagId = searchParams.get("tag") ?? "";
  /*
   * Overdue is a date question, not a status.
   *
   * Expressed as `dueBefore=now` against the API's existing filter rather than
   * as a stored flag, for the same reason readiness is computed rather than
   * saved: a task becomes overdue by the clock moving, and nothing writes to
   * it at that moment.
   */
  const overdueOnly = searchParams.get("overdue") === "1";

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // The table, calendar and timeline settings live in the URL beside the
  // filters, so a link (or a saved view) reproduces the page exactly.
  const viewState = useMemo(() => stateFromParams(searchParams, view), [searchParams, view]);
  const onViewState = useCallback(
    (change: Partial<ViewState>) => updateParams(paramsFromState({ ...viewState, ...change })),
    [viewState, updateParams],
  );
  // Field filters from the URL or a saved view; only meaningful inside one project, so the server is only asked then.
  const cfKey = projectId ? cfQueryValues(viewState.filters.custom).join("\n") : "";
  const month = searchParams.get("month") ?? undefined;
  const savedViewId = searchParams.get("sv") ?? undefined;
  const applySavedView = useCallback(
    (state: ViewState, id: string | undefined) => {
      const cleared = Object.fromEntries(VIEW_PARAM_KEYS.map((key) => [key, undefined]));
      updateParams({ ...cleared, ...paramsFromState(state), sv: id });
      rememberView(state.view);
    },
    [updateParams, rememberView],
  );

  /*
   * `/tasks` opens onto "assigned to me" rather than everyone's backlog — the
   * one default this entry point adds on top of Work's own. Only applied when
   * `mine` has never been set at all, not merely when it's currently off, so
   * following the Assigned-to-me checkbox back to "unchecked" from here still
   * sticks instead of snapping back on the next render.
   */
  useEffect(() => {
    if (personalOnly && searchParams.get("mine") === null) {
      updateParams({ mine: "1" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalOnly]);

  /* ── load the lists beside the projects ── */
  useEffect(() => {
    if (!viewer) return;
    let cancelled = false;
    api
      .get<{ lists: TaskListSummary[] }>("/task-lists")
      .then((res) => {
        if (!cancelled) setLists(res.lists);
      })
      // Lists are a secondary surface: failing to load them should not put an
      // error over the task list, which is what people came for.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [viewer]);

  /* ── load projects once ── */
  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    api
      .get<{ projects: Project[] }>("/projects")
      .then((res) => {
        if (!cancelled) setProjects(res.projects);
      })
      .catch((error: unknown) => {
        if (!cancelled)
          toast(error instanceof ApiError ? error.message : "Could not load projects.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  /** The current filters as a query string, for the exports. The server export
   *  takes `overdue` (open and past due); the task list takes `dueBefore`. */
  const exportQuery = (forExcel: boolean): string => {
    const params = new URLSearchParams();
    if (projectId) params.set("projectId", projectId);
    if (cfKey) for (const value of cfKey.split("\n")) params.append("cf", value);
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    if (search) params.set("search", search);
    if (assignedToMe) params.set("assignee", "me");
    if (tagId) params.set("tagId", tagId);
    if (overdueOnly) {
      if (forExcel) params.set("overdue", "1");
      else params.set("dueBefore", new Date().toISOString());
    }
    return params.toString();
  };

  /* ── load tasks whenever project or filters change — one fetch feeds both views ── */
  useEffect(() => {
    let cancelled = false;

    const params = new URLSearchParams();
    if (projectId) params.set("projectId", projectId);
    if (cfKey) for (const value of cfKey.split("\n")) params.append("cf", value);
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    if (search) params.set("search", search);
    if (assignedToMe) params.set("assignee", "me");
    if (tagId) params.set("tagId", tagId);
    // Anything due before this instant. Read at request time, not at render:
    // a list left open overnight should not still be answering yesterday.
    if (overdueOnly) params.set("dueBefore", new Date().toISOString());
    if (wide) params.set("limit", String(WIDE_LIMIT));

    const load = async () => {
      /*
       * A list is a different source, not a filter over the task list: its
       * contents and their order live on the list itself, and the server has
       * already dropped anything the viewer may not see.
       */
      if (listId) {
        setTasksLoading(true);
        try {
          const res = await api.get<{ tasks: Task[] }>(`/task-lists/${listId}`);
          if (!cancelled) {
            setTasks(res.tasks);
            setSelectedTaskIds(new Set());
          }
        } catch (error) {
          if (!cancelled) {
            setTasks([]);
            toast(error instanceof ApiError ? error.message : "Could not load that list.", { tone: "danger" });
          }
        } finally {
          if (!cancelled) setTasksLoading(false);
        }
        return;
      }

      setTasksLoading(true);
      try {
        const res = await api.get<{ tasks: Task[] }>(`/tasks?${params.toString()}`);
        if (!cancelled) {
          setTasks(res.tasks);
          setSelectedTaskIds(new Set());
        }
      } catch (error) {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load tasks.", { tone: "danger" });
      } finally {
        if (!cancelled) setTasksLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, listId, status, priority, search, assignedToMe, overdueOnly, tagId, wide, viewer, cfKey]);

  const refetchTask = useCallback((taskId: string) => {
    api
      .get<{ task: Task }>(`/tasks/${taskId}`)
      .then((res) => {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? res.task : t)));
      })
      .catch(() => undefined);
  }, []);

  const refetchTasks = useCallback(() => {
    const params = new URLSearchParams();
    if (projectId) params.set("projectId", projectId);
    if (cfKey) for (const value of cfKey.split("\n")) params.append("cf", value);
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    if (search) params.set("search", search);
    if (assignedToMe) params.set("assignee", "me");
    if (tagId) params.set("tagId", tagId);
    if (wide) params.set("limit", String(WIDE_LIMIT));
    api
      .get<{ tasks: Task[] }>(`/tasks?${params.toString()}`)
      .then((res) => setTasks(res.tasks))
      .catch(() => undefined);
  }, [projectId, status, priority, search, assignedToMe, tagId, wide, cfKey]);

  // The selected project's custom board columns. Null for "All projects" (no
  // single board to show) — the board then falls back to the fixed defaults.
  // Refetched when the project changes or the board is edited (bumped below).
  const [projectColumns, setProjectColumns] = useState<BoardColumnDef[] | null>(null);
  const [columnsVersion, setColumnsVersion] = useState(0);
  useEffect(() => {
    if (!projectId) {
      setProjectColumns(null);
      return;
    }
    let cancelled = false;
    api
      .get<{ columns: BoardColumnDef[] }>(`/projects/${projectId}/columns`)
      .then((res) => {
        if (!cancelled) setProjectColumns(res.columns);
      })
      .catch(() => {
        if (!cancelled) setProjectColumns(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, columnsVersion]);
  const refetchColumns = useCallback(() => setColumnsVersion((v) => v + 1), []);

  // The project's custom fields: extra table columns and CSV columns. None for "All projects".
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  useEffect(() => {
    if (!projectId) {
      setCustomFields([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ fields: CustomField[] }>(`/projects/${projectId}/custom-fields`)
      .then((res) => !cancelled && setCustomFields(res.fields))
      .catch(() => !cancelled && setCustomFields([]));
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  const setCustomValue = useCallback(
    async (task: Task, fieldId: string, value: string | number | null) => {
      try {
        const res = await api.put<{ customValues: Task["customValues"] }>(`/tasks/${task.id}/custom-fields/${fieldId}`, { value });
        setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, customValues: res.customValues } : t)));
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not save the value.", { tone: "danger" });
        throw error;
      }
    },
    [toast],
  );
  // CSV columns for the fields; person names come from the directory.
  const csvExtra = useCallback(async () => {
    if (customFields.length === 0) return undefined;
    let names = new Map<string, string>();
    if (customFields.some((f) => f.type === "person")) {
      const res = await api.get<{ people: { id: string; displayName: string }[] }>("/people").catch(() => ({ people: [] }));
      names = new Map(res.people.map((p) => [p.id, p.displayName]));
    }
    return customCsvColumns(customFields, names);
  }, [customFields]);

  // Whether the viewer may restructure this project's board: a workspace admin,
  // the project owner, or its manager. Mirrors the server's canManage.
  const selectedProject = projects.find((p) => p.id === projectId);
  const canManageColumns = Boolean(
    projectId &&
      selectedProject &&
      (viewer?.role === "admin" ||
        selectedProject.ownerId === viewer?.id ||
        selectedProject.memberRole === "manager"),
  );

  // Whether to offer inline edits and dragging on a task. A hint only: the server
  // makes the call (and a refused change rolls back), this keeps the controls
  // off tasks the viewer plainly cannot change.
  const canEditTask = useCallback(
    (task: Task): boolean => {
      if (!viewer) return false;
      if (viewer.role === "admin" || viewer.role === "manager") return true;
      if (task.projectId) {
        const project = projects.find((p) => p.id === task.projectId);
        if (!project) return false;
        return project.ownerId === viewer.id || (project.memberRole !== null && project.memberRole !== "viewer");
      }
      return task.reporterId === viewer.id || task.assignees.some((a) => a.userId === viewer.id);
    },
    [viewer, projects],
  );

  const handleAddColumn = useCallback(
    (name: string, category: BoardColumnDef["category"]) => {
      if (!projectId) return;
      api
        .post(`/projects/${projectId}/columns`, { name, category })
        .then(refetchColumns)
        .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not add the column.", { tone: "danger" }));
    },
    [projectId, refetchColumns, toast],
  );

  const handleRenameColumn = useCallback(
    (columnId: string, name: string) => {
      api
        .patch(`/columns/${columnId}`, { name })
        .then(refetchColumns)
        .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not rename the column.", { tone: "danger" }));
    },
    [refetchColumns, toast],
  );

  const handleDeleteColumn = useCallback(
    (columnId: string, name: string) => {
      const others = (projectColumns ?? []).filter((c) => c.id !== columnId);
      if (others.length === 0) {
        toast("A board needs at least one column.", { tone: "danger" });
        return;
      }
      // Tasks in the deleted column must go somewhere — the first remaining
      // column, unless the user is prompted otherwise. Kept simple: move to the
      // first other column and tell them.
      const target = others[0]!;
      if (!window.confirm(`Delete "${name}"? Its tasks move to "${target.name}".`)) return;
      api
        .delete(`/columns/${columnId}`, { moveTasksTo: target.id })
        .then(() => {
          refetchColumns();
          refetchTasks();
        })
        .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not delete the column.", { tone: "danger" }));
    },
    [projectColumns, refetchColumns, refetchTasks, toast],
  );

  const patchTask = useCallback(
    (task: Task, patch: Partial<Pick<Task, "title" | "status" | "priority" | "assigneeId" | "dueAt" | "startAt">>) => {
      const previous = task;
      // Optimistic update, reconciled or rolled back below.
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...patch } : t)));

      api
        .patch<{ task: Task }>(`/tasks/${task.id}`, { ...patch, version: task.version })
        .then((res) => {
          setTasks((prev) => prev.map((t) => (t.id === task.id ? res.task : t)));
        })
        .catch((error: unknown) => {
          setTasks((prev) => prev.map((t) => (t.id === task.id ? previous : t)));
          if (error instanceof ApiError && error.code === "VERSION_CONFLICT") {
            toast("Someone else changed this task. Refreshing it.", { tone: "danger" });
            refetchTask(task.id);
            return;
          }
          toast(error instanceof ApiError ? error.message : "Could not update task.", { tone: "danger" });
        });
    },
    [toast, refetchTask],
  );

  const handleToggleDone = useCallback(
    (task: Task) => {
      patchTask(task, { status: task.status === "done" ? "todo" : "done" });
    },
    [patchTask],
  );

  const handleRename = useCallback(
    (task: Task, title: string) => {
      patchTask(task, { title });
    },
    [patchTask],
  );

  const handleMove = useCallback(
    (task: Task, status: Task["status"]) => {
      if (status === task.status) return;
      patchTask(task, { status });
    },
    [patchTask],
  );

  // Open the confirm dialog for a single-task delete from a row's ··· menu.
  const handleDelete = useCallback((task: Task) => setDeletingTask(task), []);

  const confirmDeleteTask = useCallback(() => {
    if (!deletingTask) return;
    const task = deletingTask;
    setDeleteTaskBusy(true);
    api
      .delete(`/tasks/${task.id}`)
      .then(() => {
        toast(`"${task.title}" deleted.`);
        setDeletingTask(null);
        // Drop it from the list immediately; a refetch would also do, but this
        // is instant and the row is gone either way.
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
      })
      .catch((error: unknown) => {
        toast(
          error instanceof ApiError ? error.message : "Could not delete that task.",
          { tone: "danger" },
        );
      })
      .finally(() => setDeleteTaskBusy(false));
  }, [deletingTask, toast]);

  const handleDropInColumn = useCallback(
    (
      taskId: string,
      changes: BoardColumn["apply"],
      afterTaskId: string | null,
      beforeTaskId: string | null,
    ) => {
      if (!changes) return;
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;
      const previousTasks = tasks;

      // Moved immediately: the server's reply carries only a position number,
      // so the optimistic update is what the person actually sees happen.
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...changes } : t)));

      /*
       * Two different requests, because the board is grouped two different
       * ways. Grouping by status moves a card *within the board's own
       * ordering*, which is what `/reorder` exists for. Grouping by anything
       * else changes a field, and ordering within such a column is incidental —
       * a plain edit says what happened more honestly than a reorder that also
       * quietly rewrites a due date.
       */
      const request =
        changes.columnId !== undefined
          ? // Custom-column board: the move is by column; the server sets the
            // status from the column's category.
            api
              .post<{ rebalanced: boolean }>(`/tasks/${taskId}/reorder`, {
                columnId: changes.columnId,
                afterTaskId,
                beforeTaskId,
              })
              .then((res) => {
                if (res.rebalanced) refetchTasks();
              })
          : changes.status !== undefined
            ? api
                .post<{ rebalanced: boolean }>(`/tasks/${taskId}/reorder`, {
                  status: changes.status,
                  afterTaskId,
                  beforeTaskId,
                })
                .then((res) => {
                  if (res.rebalanced) refetchTasks();
                })
            : api.patch(`/tasks/${taskId}`, changes).then(() => undefined);

      request.catch((error: unknown) => {
        setTasks(previousTasks);
        toast(error instanceof ApiError ? error.message : "Could not move task.", { tone: "danger" });
      });
    },
    [tasks, toast, refetchTasks],
  );

  const handleToggleSelected = useCallback((task: Task) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(task.id)) next.delete(task.id);
      else next.add(task.id);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedTaskIds((prev) => (prev.size === tasks.length ? new Set() : new Set(tasks.map((t) => t.id))));
  }, [tasks]);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedTaskIds(new Set());
  }, []);

  const openBulkReassign = useCallback(() => {
    setBulkReassigning(true);
    setBulkAssignees(null);
    api
      .get<{ people: { id: string; displayName: string }[] }>("/people")
      .then((res) => setBulkAssignees(res.people))
      .catch(() => setBulkAssignees([]));
  }, []);

  const confirmBulkReassign = useCallback(
    (assigneeId: string) => {
      const ids = [...selectedTaskIds];
      if (ids.length === 0) return;
      setBulkReassignBusy(true);
      api
        .patch<{ updated: number }>("/tasks/bulk-assign", { taskIds: ids, assigneeId })
        .then((res) => {
          toast(
            res.updated < ids.length
              ? `${res.updated} of ${ids.length} tasks reassigned. The rest are not yours to edit.`
              : `${res.updated} ${res.updated === 1 ? "task" : "tasks"} reassigned.`,
          );
          setBulkReassigning(false);
          setSelectedTaskIds(new Set());
          refetchTasks();
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not reassign those tasks.", { tone: "danger" });
        })
        .finally(() => setBulkReassignBusy(false));
    },
    [selectedTaskIds, toast, refetchTasks],
  );

  const confirmBulkClose = useCallback(() => {
    const ids = [...selectedTaskIds];
    if (ids.length === 0) return;
    setBulkCloseBusy(true);
    api
      .patch<{ updated: number }>("/tasks/bulk-status", { taskIds: ids, status: "done" })
      .then((res) => {
        toast(
          res.updated < ids.length
            ? `${res.updated} of ${ids.length} tasks closed. The rest are not yours to edit.`
            : `${res.updated} ${res.updated === 1 ? "task" : "tasks"} closed.`,
        );
        setBulkClosing(false);
        setSelectedTaskIds(new Set());
        refetchTasks();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not close those tasks.", { tone: "danger" });
      })
      .finally(() => setBulkCloseBusy(false));
  }, [selectedTaskIds, toast, refetchTasks]);

  const confirmBulkDelete = useCallback(() => {
    const ids = [...selectedTaskIds];
    if (ids.length === 0) return;
    setBulkDeleteBusy(true);
    api
      .delete<{ deleted: number }>("/tasks/bulk", { taskIds: ids })
      .then((res) => {
        toast(
          res.deleted < ids.length
            ? `${res.deleted} of ${ids.length} tasks deleted. The rest are not yours to delete.`
            : `${res.deleted} ${res.deleted === 1 ? "task" : "tasks"} deleted.`,
        );
        setBulkDeleting(false);
        setSelectedTaskIds(new Set());
        refetchTasks();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not delete those tasks.", { tone: "danger" });
      })
      .finally(() => setBulkDeleteBusy(false));
  }, [selectedTaskIds, toast, refetchTasks]);

  /*
   * No bulk-add endpoint on the server — unlike reassign/close/delete, which
   * each apply one change to many rows in one query, adding to a list is a
   * set of independent inserts (one row per task, each re-checking that
   * viewer's own read access to that specific task), so there is nothing a
   * single batched statement would save. Settled rather than awaited one at
   * a time so one slow or refused task does not hold up the rest; a
   * conflict (already on the list) is a success at the server, not an
   * error, so only genuine failures are counted against the total.
   */
  const confirmBulkAddToList = useCallback(
    (targetListId: string) => {
      const ids = [...selectedTaskIds];
      if (ids.length === 0) return;
      const list = lists.find((l) => l.id === targetListId);
      setBulkAddingToList(false);
      Promise.allSettled(ids.map((taskId) => api.post(`/task-lists/${targetListId}/items`, { taskId }))).then(
        (results) => {
          const succeeded = results.filter((r) => r.status === "fulfilled").length;
          setLists((prev) =>
            prev.map((l) => (l.id === targetListId ? { ...l, itemCount: l.itemCount + succeeded } : l)),
          );
          toast(
            succeeded < ids.length
              ? `${succeeded} of ${ids.length} tasks added${list ? ` to "${list.name}"` : ""}.`
              : `${succeeded} ${succeeded === 1 ? "task" : "tasks"} added${list ? ` to "${list.name}"` : ""}.`,
          );
          setSelectedTaskIds(new Set());
          // Currently viewing this same list: reflect the additions
          // immediately rather than waiting for the next refetch.
          if (targetListId === listId) refetchTasks();
        },
      );
    },
    [selectedTaskIds, lists, listId, toast, refetchTasks],
  );

  const handleAddTask = useCallback(
    (columnApply: BoardColumn["apply"], title: string) => {
      if (!viewer) return;
      // The new task is born already belonging to the column it was typed into
      // — created under "Today" it is due today, under "High" it is high.
      api
        .post<{ task: Task }>("/tasks", { title, projectId, ...(columnApply ?? {}) })
        .then((res) => {
          setTasks((prev) => [...prev, res.task]);
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not create task.", { tone: "danger" });
        });
    },
    [projectId, viewer, toast],
  );

  /** Opens the full dialog, carrying over whatever was typed inline. */
  const handleExpandTask = useCallback((columnApply: BoardColumn["apply"], title: string) => {
    setDetailDraft(title);
    // The dialog only carries a status; other column meanings (a due date, a
    // priority) are set in the dialog itself, where they are visible and
    // editable rather than applied invisibly.
    setDetailStatus((columnApply?.status as Task["status"]) ?? "todo");
    setDetailOpen(true);
  }, []);

  const handleProjectCreated = useCallback(
    (project: Project) => {
      setProjects((prev) => [project, ...prev]);
      setCreatingProject(false);
      updateParams({ project: project.id });
    },
    [updateParams],
  );

  /**
   * Move to another project, dropping the task filters on the way.
   *
   * The filters describe a question about the list you were looking at, not a
   * property of the project you are moving to. Carrying them across is how
   * somebody arrives from Home's "Assigned to you" tile — which links to
   * `/work?mine=1` — clicks a project with seven tasks, and is told none match:
   * the filter is still on, and the empty state reads as missing data rather
   * than as a filter that needs clearing.
   */
  const selectList = useCallback(
    (id: string | undefined) => {
      // A list and a project are alternative ways of looking at tasks, so
      // choosing one clears the other rather than intersecting them, which
      // would show a confusing subset nobody asked for.
      updateParams({
        list: id,
        project: undefined,
        q: undefined,
        status: undefined,
        priority: undefined,
        mine: undefined,
        overdue: undefined,
      });
    },
    [updateParams],
  );

  /** Returns the new list's id on success, so a caller that also wants to
   *  add a task to it (see handleCreateListAndAdd below) has something to
   *  add to — undefined on cancel or failure, both already toasted/no-op'd
   *  here so callers don't need their own error handling for this step. */
  const createList = useCallback(
    async (options: { selectAfter?: boolean } = {}): Promise<string | undefined> => {
      const name = window.prompt("Name this list");
      if (!name?.trim()) return undefined;
      try {
        const res = await api.post<{ list: TaskListSummary }>("/task-lists", { name: name.trim() });
        setLists((prev) => [...prev, res.list].sort((a, b) => a.name.localeCompare(b.name)));
        if (options.selectAfter ?? true) selectList(res.list.id);
        return res.list.id;
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not create the list.", { tone: "danger" });
        return undefined;
      }
    },
    [selectList, toast],
  );

  const handleAddToList = useCallback(
    (task: Task, targetListId: string) => {
      api
        .post(`/task-lists/${targetListId}/items`, { taskId: task.id })
        .then(() => {
          const list = lists.find((l) => l.id === targetListId);
          toast(list ? `Added to "${list.name}".` : "Added to the list.");
          // Bumps the sidebar's own count rather than a full refetch —
          // adding is a no-op server-side if the task was already on the
          // list, so incrementing here can overcount by one in that one
          // case; a stale-by-one badge is a smaller cost than a network
          // round trip for every add.
          setLists((prev) => prev.map((l) => (l.id === targetListId ? { ...l, itemCount: l.itemCount + 1 } : l)));
          // Currently viewing this same list: reflect the addition
          // immediately rather than waiting for the next refetch.
          if (targetListId === listId) refetchTasks();
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not add that task to the list.", { tone: "danger" });
        });
    },
    [lists, listId, toast, refetchTasks],
  );

  const handleCreateListAndAdd = useCallback(
    async (task: Task) => {
      // The prompt-driven list creation already navigates into the new list
      // by default; skipped here so the person stays on the board or list
      // they were working from — the point of adding a task to a list from
      // there is to keep going, not to be dropped into an empty new view.
      const newListId = await createList({ selectAfter: false });
      if (newListId) handleAddToList(task, newListId);
    },
    [createList, handleAddToList],
  );

  const bulkCreateListAndAdd = useCallback(async () => {
    const newListId = await createList({ selectAfter: false });
    if (newListId) confirmBulkAddToList(newListId);
  }, [createList, confirmBulkAddToList]);

  const selectProject = useCallback(
    (id: string | undefined) => {
      updateParams({
        project: id,
        list: undefined,
        q: undefined,
        status: undefined,
        priority: undefined,
        mine: undefined,
        overdue: undefined,
      });
    },
    [updateParams],
  );

  const activeProject = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const activeList = useMemo(() => lists.find((l) => l.id === listId), [lists, listId]);
  const showProjectCode = !projectId;

  usePageTitle(
    personalOnly ? "My tasks" : activeList ? activeList.name : activeProject ? activeProject.name : "Work",
  );

  const [allTags] = useTags();

  /* ── sections: named groups in the project's list view ── */
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const loadSections = useCallback(() => {
    if (!projectId) {
      setSections([]);
      return;
    }
    api
      .get<{ sections: ProjectSection[] }>(`/projects/${projectId}/sections`)
      .then((res) => setSections(res.sections))
      .catch(() => setSections([]));
  }, [projectId]);
  useEffect(loadSections, [loadSections]);

  const sectionError = (e: unknown) => toast(e instanceof ApiError ? e.message : "Could not change the section.", { tone: "danger" });

  const sectionControls: SectionControls | undefined = projectId
    ? {
        sections,
        // Mirrors who may write the project's tasks; the server decides.
        canManage: Boolean(
          selectedProject && (viewer?.role === "admin" || selectedProject.ownerId === viewer?.id || (selectedProject.memberRole && selectedProject.memberRole !== "viewer")),
        ),
        onCreate: (name) => {
          api.post(`/projects/${projectId}/sections`, { name }).then(loadSections).catch(sectionError);
        },
        onRename: (section, name) => {
          api.patch(`/sections/${section.id}`, { name }).then(loadSections).catch(sectionError);
        },
        onMove: (section, by) => {
          const ids = sections.map((s) => s.id);
          const from = ids.indexOf(section.id);
          const to = from + by;
          if (to < 0 || to >= ids.length) return;
          [ids[from], ids[to]] = [ids[to]!, ids[from]!];
          // Shown at once; the reload settles it against the server's order.
          setSections(ids.map((id, position) => ({ ...sections.find((s) => s.id === id)!, position })));
          api.post(`/projects/${projectId}/sections/reorder`, { sectionIds: ids }).then(loadSections).catch(sectionError);
        },
        onDelete: (section) => {
          api
            .delete(`/sections/${section.id}`)
            .then(() => {
              // The server leaves its tasks without a section; follow suit locally.
              setTasks((prev) => prev.map((t) => (t.sectionId === section.id ? { ...t, sectionId: null, sectionName: null } : t)));
              loadSections();
            })
            .catch(sectionError);
        },
        onAddTask: (section, title) => {
          api
            .post<{ task: Task }>("/tasks", { title, projectId, sectionId: section.id })
            .then((res) => setTasks((prev) => [...prev, res.task]))
            .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not create task.", { tone: "danger" }));
        },
      }
    : undefined;
  const isEmptyResult = !tasksLoading && tasks.length === 0;
  const hasActiveFilters = !!(search || status || priority || assignedToMe || overdueOnly || tagId);

  /*
   * Name the filters that are hiding things.
   *
   * "Try adjusting your search or filters" does not say which filter, so a
   * project with seven tasks and `mine=1` still set reads as an empty project.
   * Spelling out the active ones turns a dead end into an instruction.
   */
  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (search) parts.push(`matching “${search}”`);
    if (status) parts.push(`with status ${status.split(",").join(" or ")}`);
    if (priority) parts.push(`at ${priority} priority`);
    if (assignedToMe) parts.push("assigned to you");
    if (overdueOnly) parts.push("overdue");
    if (tagId) parts.push(`tagged ${allTags.find((t) => t.id === tagId)?.name ?? "with that tag"}`);
    if (parts.length === 0) return "";
    return `No tasks here are ${parts.join(", ")}.`;
  }, [search, status, priority, assignedToMe, overdueOnly, tagId, allTags]);

  return (
    <div className={`work-shell ${personalOnly ? "work-shell--personal" : ""}`}>
      {!personalOnly && (
        <ProjectSidebar
          projects={projects}
          loading={projectsLoading}
          activeProjectId={projectId}
          onSelect={selectProject}
          onCreateClick={() => setCreatingProject(true)}
          lists={lists}
          activeListId={listId}
          onSelectList={selectList}
          onCreateList={createList}
        />
      )}

      <section className="work-main" aria-label="Tasks">
        <PageHeader
          className="work-main__header"
          /* Only reachable on a narrow screen, where the project sidebar is
             hidden; CSS keeps it out of the way on desktop. Opens the project
             list as a sheet — a chevron alone gave no way back into it, only
             a way to clear the current project, so switching projects was
             unreachable on mobile once the sidebar had nowhere left to sit.
             `/tasks` has no project sidebar to open, so the button is not
             rendered there at all rather than opening an empty sheet. */
          leading={
            personalOnly ? undefined : (
              <button
                type="button"
                className="work-main__back"
                onClick={() => setMobileProjectsOpen(true)}
                aria-label="Browse projects"
              >
                <Icon name="chevron-left" size={18} />
              </button>
            )
          }
          title={
            personalOnly
              ? "My Tasks"
              : activeList
                ? activeList.name
                : activeProject
                  ? `${activeProject.code} · ${activeProject.name}`
                  : "All projects"
          }
          /* The considered path: brief, owner, dates, attachments. The inline
             rows below stay for capturing a title quickly. */
          actions={
            <>
              {view === "list" && tasks.length > 0 && (
                <Button
                  variant={selectionMode ? "primary" : "secondary"}
                  size="sm"
                  icon={<Icon name="checklist" size={14} />}
                  onClick={() => (selectionMode ? exitSelectionMode() : setSelectionMode(true))}
                >
                  {selectionMode ? "Done selecting" : "Select"}
                </Button>
              )}
              {/* A saved list is a different source from the task query, so the
                  server export (which re-runs the filters) cannot reproduce it:
                  those keep the CSV of what is on screen. */}
              {tasks.length > 0 &&
                (listId ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Icon name="download" size={14} />}
                    onClick={async () => exportTasksCsv(tasks, await csvExtra())}
                  >
                    Export CSV
                  </Button>
                ) : (
                  <ExportMenu
                    kind="tasks"
                    excelPath={`/exports/tasks.xlsx?${exportQuery(true)}`}
                    onCsv={async () => {
                      // Asks for every match, not the 200 the screen loads.
                      const res = await api.get<{ tasks: Task[] }>(`/tasks?${exportQuery(false)}&limit=all`);
                      exportTasksCsv(res.tasks, await csvExtra());
                    }}
                  />
                ))}
              <Button
                size="sm"
                className="work-main__new-task"
                icon={<Icon name="plus" size={14} />}
                onClick={() => {
                  setDetailDraft("");
                  setDetailStatus("todo");
                  setDetailOpen(true);
                }}
              >
                New task
              </Button>
            </>
          }
        />

        <FilterBar
          search={search}
          onSearchChange={(value) => updateParams({ q: value || undefined })}
          status={status}
          onStatusChange={(value) => updateParams({ status: value || undefined })}
          priority={priority}
          onPriorityChange={(value) => updateParams({ priority: value || undefined })}
          assignedToMe={assignedToMe}
          onAssignedToMeChange={(value) => updateParams({ mine: value ? "1" : undefined })}
          overdueOnly={overdueOnly}
          onOverdueChange={(value) => updateParams({ overdue: value ? "1" : undefined })}
          tags={allTags}
          tagId={tagId}
          onTagChange={(value) => updateParams({ tag: value || undefined })}
          view={view}
          onViewChange={(value) => {
            rememberView(value);
            updateParams({ view: value, sv: undefined });
          }}
          extra={
            <SavedViewsMenu
              current={{ ...viewState, grouping: view === "board" ? grouping : viewState.grouping }}
              projectId={projectId}
              canShare={Boolean(
                projectId &&
                  selectedProject &&
                  (viewer?.role === "admin" ||
                    selectedProject.ownerId === viewer?.id ||
                    (selectedProject.memberRole && selectedProject.memberRole !== "viewer")),
              )}
              activeId={savedViewId}
              onApply={applySavedView}
            />
          }
          grouping={grouping}
          onGroupingChange={(value) => {
            rememberGroup(value);
            updateParams({ group: value });
          }}
        />

        {view === "list" && selectionMode && selectedTaskIds.size > 0 && (
          <div className="work-bulk-toolbar">
            <span className="work-bulk-toolbar__count">{selectedTaskIds.size} selected</span>
            <Button size="sm" variant="secondary" onClick={openBulkReassign}>
              Reassign
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setBulkClosing(true)}>
              Close
            </Button>
            <Menu
              open={bulkAddingToList}
              onClose={() => setBulkAddingToList(false)}
              label="Add to list"
              items={[
                ...lists.map((list) => ({
                  id: list.id,
                  label: list.name,
                  onSelect: () => confirmBulkAddToList(list.id),
                })),
                { id: "new", label: "New list…", icon: "plus" as const, onSelect: () => void bulkCreateListAndAdd() },
              ]}
            >
              <Button size="sm" variant="secondary" onClick={() => setBulkAddingToList((v) => !v)}>
                Add to list
              </Button>
            </Menu>
            <Button size="sm" variant="danger" onClick={() => setBulkDeleting(true)}>
              Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedTaskIds(new Set())}>
              Clear
            </Button>
          </div>
        )}

        <div
          className={`work-content ${view === "list" && isEmptyResult ? "work-content--centered" : ""} ${
            view === "table" ? "work-content--fill" : ""
          }`}
        >
          {tasksLoading ? (
            <div className="work-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="work-loading__row">
                  <Skeleton circle width={18} height={18} />
                  <SkeletonText lines={1} />
                </div>
              ))}
            </div>
          ) : view === "list" ? (
            isEmptyResult ? (
              <EmptyState
                icon="work"
                title={hasActiveFilters ? "No tasks match" : "No tasks yet"}
                description={hasActiveFilters ? activeFilterSummary : "Add a task below to get started."}
                action={
                  !hasActiveFilters ? undefined : (
                    <Button size="sm" variant="secondary" onClick={() => updateParams({ q: undefined, status: undefined, priority: undefined, mine: undefined, overdue: undefined, tag: undefined })}>
                      Clear filters
                    </Button>
                  )
                }
              />
            ) : (
              <ListView
                tasks={tasks}
                showProjectCode={showProjectCode}
                onToggleDone={handleToggleDone}
                onRename={handleRename}
                onMove={handleMove}
                onDelete={handleDelete}
                onAddTask={(title) => handleAddTask({ status: "todo" }, title)}
                onExpandTask={(title) => handleExpandTask({ status: "todo" }, title)}
                selectionMode={selectionMode}
                selectedIds={selectedTaskIds}
                onToggleSelected={handleToggleSelected}
                onToggleSelectAll={handleToggleSelectAll}
                lists={lists}
                onAddToList={handleAddToList}
                onCreateListAndAdd={handleCreateListAndAdd}
                sectionControls={sectionControls}
              />
            )
          ) : wide ? (
            isEmptyResult ? (
              <EmptyState
                icon="work"
                title={hasActiveFilters ? "No tasks match" : "No tasks yet"}
                description={hasActiveFilters ? activeFilterSummary : "Switch to List to add the first task."}
              />
            ) : (
              <>
                {view === "table" && (
                  <TableView
                    tasks={tasks}
                    canEdit={canEditTask}
                    onPatch={patchTask as (task: Task, patch: TaskPatch) => void}
                    state={viewState}
                    onState={onViewState}
                    customFields={customFields}
                    onCustomValue={setCustomValue}
                  />
                )}
                {view === "calendar" && (
                  <CalendarView
                    tasks={tasks}
                    canEdit={canEditTask}
                    onPatch={patchTask as (task: Task, patch: TaskPatch) => void}
                    month={month}
                    onMonthChange={(value) => updateParams({ month: value })}
                  />
                )}
                {view === "timeline" && (
                  <TimelineView
                    tasks={tasks}
                    canEdit={canEditTask}
                    onPatch={patchTask as (task: Task, patch: TaskPatch) => void}
                    state={viewState}
                    onState={onViewState}
                  />
                )}
                {tasks.length >= WIDE_LIMIT && (
                  <p className="tv-note">Showing the first {WIDE_LIMIT} tasks. Narrow the filters to see the rest.</p>
                )}
              </>
            )
          ) : (
            // Board keeps its columns (and per-column add-task) visible even
            // with zero matches, so filtering to nothing doesn't block adding
            // a first task in a specific status.
            <BoardView
              tasks={tasks}
              showProjectCode={showProjectCode}
              grouping={grouping}
              projectColumns={projectColumns}
              canManageColumns={canManageColumns}
              onAddColumn={handleAddColumn}
              onRenameColumn={handleRenameColumn}
              onDeleteColumn={handleDeleteColumn}
              onDropInColumn={handleDropInColumn}
              onMove={handleMove}
              onDelete={handleDelete}
              onAddTask={handleAddTask}
              onExpandTask={handleExpandTask}
              lists={lists}
              onAddToList={handleAddToList}
              onCreateListAndAdd={handleCreateListAndAdd}
            />
          )}
        </div>
      </section>

      {/* The shared Modal rather than a bespoke overlay: it carries the focus
          trap, Escape handling, and dialog semantics that a hand-rolled div
          silently omits. */}
      <Modal open={creatingProject} onClose={() => setCreatingProject(false)} title="New project">
        <NewProjectForm onCreated={handleProjectCreated} onCancel={() => setCreatingProject(false)} />
      </Modal>

      <NewTaskDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        projectId={projectId ?? null}
        status={detailStatus}
        initialTitle={detailDraft}
        onCreated={(task) => {
          // Filters may exclude the new task (e.g. "assigned to me" while it
          // went to someone else); refetching keeps the list honest rather
          // than showing a row the current filters say should not be there.
          if (hasActiveFilters) refetchTasks();
          else setTasks((prev) => [...prev, task as Task]);
        }}
      />

      <Modal open={bulkReassigning} onClose={() => setBulkReassigning(false)} title={`Reassign ${selectedTaskIds.size} tasks`}>
        <div className="work-new-project">
          {bulkAssignees === null ? (
            <p className="work-sidebar__hint">Loading people…</p>
          ) : (
            <Select
              label="Assign to"
              value=""
              onChange={(event) => {
                if (event.target.value) confirmBulkReassign(event.target.value);
              }}
              disabled={bulkReassignBusy}
            >
              <option value="">Choose someone…</option>
              {bulkAssignees.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName}
                </option>
              ))}
            </Select>
          )}
          <div className="work-new-project__actions">
            <Button type="button" variant="ghost" onClick={() => setBulkReassigning(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={bulkClosing} onClose={() => setBulkClosing(false)} title="Close these tasks?">
        <div className="work-confirm">
          <p>
            {selectedTaskIds.size} {selectedTaskIds.size === 1 ? "task" : "tasks"} will be marked done.
          </p>
          <div className="work-confirm-actions">
            <Button variant="ghost" onClick={() => setBulkClosing(false)}>
              Cancel
            </Button>
            <Button loading={bulkCloseBusy} onClick={confirmBulkClose}>
              Close tasks
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={bulkDeleting} onClose={() => setBulkDeleting(false)} title="Delete these tasks?">
        <div className="work-confirm">
          <p>
            {selectedTaskIds.size} {selectedTaskIds.size === 1 ? "task" : "tasks"} will be deleted. Subtasks are kept and
            promoted to the top level, the same as deleting one task at a time. This cannot be undone.
          </p>
          <div className="work-confirm-actions">
            <Button variant="ghost" onClick={() => setBulkDeleting(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={bulkDeleteBusy} onClick={confirmBulkDelete}>
              Delete tasks
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!deletingTask} onClose={() => setDeletingTask(null)} title="Delete this task?">
        <div className="work-confirm">
          <p>
            &ldquo;{deletingTask?.title}&rdquo; will be deleted. Any subtasks are kept and promoted to the top level.
            This cannot be undone.
          </p>
          <div className="work-confirm-actions">
            <Button variant="ghost" onClick={() => setDeletingTask(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={deleteTaskBusy} onClick={confirmDeleteTask}>
              Delete task
            </Button>
          </div>
        </div>
      </Modal>

      {/* Mobile-only: the same ProjectSidebar the desktop rail shows beside
          the task list, opened as a sheet instead because there is no room
          for a second column below 900px. */}
      <Sheet open={mobileProjectsOpen} onClose={() => setMobileProjectsOpen(false)} title="Projects">
        <ProjectSidebar
          projects={projects}
          loading={projectsLoading}
          activeProjectId={projectId}
          onSelect={(id) => {
            selectProject(id);
            setMobileProjectsOpen(false);
          }}
          lists={lists}
          activeListId={listId}
          onSelectList={(id) => {
            selectList(id);
            setMobileProjectsOpen(false);
          }}
          onCreateList={() => {
            setMobileProjectsOpen(false);
            void createList();
          }}
          onCreateClick={() => {
            setMobileProjectsOpen(false);
            setCreatingProject(true);
          }}
        />
      </Sheet>
    </div>
  );
}
